let placesPromise = null;

const waitForMaps = (timeoutMs = 4000) => {
    if (window.google?.maps) {
        return Promise.resolve(window.google.maps);
    }
    return new Promise((resolve) => {
        const started = Date.now();
        const timer = window.setInterval(() => {
            if (window.google?.maps) {
                window.clearInterval(timer);
                resolve(window.google.maps);
                return;
            }
            if (Date.now() - started >= timeoutMs) {
                window.clearInterval(timer);
                resolve(null);
            }
        }, 50);
    });
};

const loadMapsScript = () => {
    const existing = document.getElementById('sm-google-maps-places');
    if (existing) {
        return waitForMaps(8000);
    }
    const key = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY;
    if (!key) {
        return Promise.resolve(null);
    }
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.id = 'sm-google-maps-places';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
        script.async = true;
        script.onload = () => resolve(window.google?.maps || null);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
    });
};

export const loadGooglePlaces = async () => {
    if (window.google?.maps?.places) {
        return window.google.maps.places;
    }
    if (!placesPromise) {
        placesPromise = (async () => {
            let maps = window.google?.maps || await waitForMaps();
            if (maps?.places) {
                return maps.places;
            }
            if (maps?.importLibrary) {
                const lib = await maps.importLibrary('places');
                return lib || window.google?.maps?.places || null;
            }
            maps = await loadMapsScript();
            return maps?.places || window.google?.maps?.places || null;
        })().catch(() => null);
    }
    return placesPromise;
};

const boundInputs = new WeakSet();
const searchTimers = new WeakMap();

const sharedSuggestList = () => {
    let list = document.getElementById('sm-place-suggest-live');
    if (!list) {
        list = document.createElement('ul');
        list.id = 'sm-place-suggest-live';
        list.className = 'sm-place-suggest';
        list.hidden = true;
        document.body.appendChild(list);
    }
    return list;
};

const hideSuggestList = (list) => {
    if (list) {
        list.hidden = true;
        list.replaceChildren();
    }
};

const renderSuggestList = (list, suggestions, input, hidden) => {
    list.replaceChildren();
    if (!suggestions.length || !document.contains(input)) {
        hideSuggestList(list);
        return;
    }
    const rect = input.getBoundingClientRect();
    list.style.top = `${rect.bottom + 4}px`;
    list.style.left = `${rect.left}px`;
    list.style.width = `${Math.max(rect.width, 240)}px`;
    suggestions.forEach((item) => {
        const row = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = item.label;
        button.addEventListener('mousedown', (event) => {
            event.preventDefault();
            event.stopPropagation();
            input.dataset.smPlacePicked = item.label;
            input.value = item.label;
            if (hidden) {
                hidden.value = item.label;
            }
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
            hideSuggestList(list);
        });
        row.appendChild(button);
        list.appendChild(row);
    });
    list.hidden = false;
};

const schedulePlaceSearch = (input, hidden, list) => {
    window.clearTimeout(searchTimers.get(input));
    const handle = window.setTimeout(async () => {
        const { suggestions } = await fetchPlacePredictions(input.value);
        renderSuggestList(list, suggestions, input, hidden);
    }, 250);
    searchTimers.set(input, handle);
};

export const bindLocationInput = async (input, hidden) => {
    if (!input || boundInputs.has(input)) {
        return false;
    }
    boundInputs.add(input);
    const list = sharedSuggestList();
    const onInput = () => {
        if (hidden) {
            hidden.value = input.value;
        }
        if (input.dataset.smPlacePicked === input.value) {
            delete input.dataset.smPlacePicked;
            hideSuggestList(list);
            return;
        }
        schedulePlaceSearch(input, hidden, list);
    };
    input.addEventListener('input', onInput);
    input.addEventListener('focus', () => {
        if (String(input.value || '').trim().length >= 3) {
            schedulePlaceSearch(input, hidden, list);
        }
    });
    return true;
};

export const resetPlacesCache = () => {
    placesPromise = null;
};

export const describePlacesReady = async () => {
    const places = await loadGooglePlaces();
    return {
        hasKey: Boolean(import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY),
        mapsReady: Boolean(window.google?.maps),
        placesReady: Boolean(places),
        classicService: Boolean(places?.AutocompleteService),
        classicWidget: Boolean(places?.Autocomplete),
        newSuggestions: Boolean(places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions),
    };
};

const suggestionFromClassic = (prediction) => ({
    id: prediction.place_id || prediction.description,
    label: prediction.description || '',
});

const suggestionFromNew = (item) => {
    const prediction = item?.placePrediction;
    const label = prediction?.text?.text || item?.text?.text || '';
    return {
        id: prediction?.placeId || label,
        label,
    };
};

export const fetchPlacePredictions = async (query) => {
    const input = String(query || '').trim();
    if (input.length < 3) {
        return { suggestions: [], status: 'idle', source: null };
    }
    const places = await loadGooglePlaces();
    if (!places) {
        return { suggestions: [], status: 'maps-missing', source: null };
    }
    if (places.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
        try {
            const result = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input });
            const suggestions = (result?.suggestions || []).map(suggestionFromNew).filter((item) => item.label);
            return {
                suggestions,
                status: suggestions.length ? 'OK' : 'ZERO_RESULTS',
                source: 'new',
            };
        } catch (error) {
            return {
                suggestions: [],
                status: error?.message || 'new-api-failed',
                source: 'new',
            };
        }
    }
    if (places.AutocompleteService) {
        return new Promise((resolve) => {
            const service = new places.AutocompleteService();
            service.getPlacePredictions({ input }, (predictions, status) => {
                const suggestions = (predictions || []).map(suggestionFromClassic).filter((item) => item.label);
                resolve({
                    suggestions,
                    status: String(status || 'UNKNOWN'),
                    source: 'classic',
                });
            });
        });
    }
    return { suggestions: [], status: 'no-autocomplete-api', source: null };
};

export const bindEditorLocationField = (root) => {
    if (!root?.querySelector) {
        return;
    }
    const connect = () => {
        const input = root.querySelector('#VenueSearch, .sm-place-input');
        const hidden = root.querySelector('#Venue');
        bindLocationInput(input, hidden);
    };
    connect();
    window.setTimeout(connect, 80);
    window.setTimeout(connect, 300);
};
